import {useCallback, useEffect, useRef, useState} from "react"
import {taskService} from "services"
import {TaskStatus} from "types/tasks"

export const IN_FLIGHT_STATUSES = [TaskStatus.PROCESSING, TaskStatus.PENDING_REVIEW]

// All the timings of the processing state (ms)
export const TASK_USER_POLLING = {
    // delay between polls while the elapsed time is lower than `until`; the last step has no limit
    steps: [
        {until: 30 * 1000, delay: 3000},
        {until: 60 * 1000, delay: 5000},
        {until: Infinity, delay: 10000}
    ],
    // after this time the polling stops until the user refreshes
    stopAfter: 5 * 60 * 1000
}

const getPollingDelay = (elapsed: number) => TASK_USER_POLLING.steps.find((step) => elapsed < step.until).delay

type Props = {
    taskId: number
    subTaskId?: number | string
    taskUserId: number | string
    enabled: boolean
    onSettled: (status: TaskStatus) => void
}

/**
 * Polls the task user until it leaves `processing` / `pending_review`.
 * Reads `/subtasks/list` filtered by subtask (no answers in the response), as there is no endpoint to get a
 * single task user. Only the task user `status` is used, never the subtask-level one.
 * Polling pauses while the tab is hidden, never runs two requests at once and stops after `stopAfter`.
 */
export function useTaskUserPolling({taskId, subTaskId, taskUserId, enabled, onSettled}: Props) {
    const [isChecked, setIsChecked] = useState(false)
    const [isTimedOut, setIsTimedOut] = useState(false)
    const onSettledRef = useRef(onSettled)
    onSettledRef.current = onSettled
    const refreshRef = useRef<() => void>(() => {})

    useEffect(() => {
        if (!enabled || !taskId || !taskUserId) {
            return
        }
        setIsChecked(false)
        setIsTimedOut(false)
        let timeoutId: ReturnType<typeof setTimeout>
        let startedAt = Date.now()
        let isCancelled = false
        let isRequesting = false
        let isSettled = false
        let isStopped = false

        const fetchStatus = async () => {
            const filter: any = subTaskId ? {subtaskId: +subTaskId} : {}
            const pageSize = subTaskId ? 1 : 1000
            const {data} = await taskService.getNewSubTasks(taskId, {filter, range: {page: 1, pageSize}})
            const users = (data || []).flatMap((subTask) => subTask.users || [])
            return users.find((item) => item.id === taskUserId)?.status as TaskStatus
        }

        const poll = async () => {
            clearTimeout(timeoutId)
            if (isCancelled || isSettled || isRequesting) {
                return
            }
            isRequesting = true
            let status: TaskStatus
            try {
                status = await fetchStatus()
            } catch (error) {
                // transient errors are ignored, the next tick retries until the polling stops
                console.error(error)
            }
            isRequesting = false
            if (isCancelled) {
                return
            }
            if (status && !IN_FLIGHT_STATUSES.includes(status)) {
                isSettled = true
                onSettledRef.current(status)
                return
            }
            setIsChecked(true)
            const elapsed = Date.now() - startedAt
            if (elapsed >= TASK_USER_POLLING.stopAfter) {
                isStopped = true
                setIsTimedOut(true)
                return
            }
            if (!document.hidden) {
                timeoutId = setTimeout(poll, getPollingDelay(elapsed))
            }
        }

        const onVisibilityChange = () => {
            if (!document.hidden && !isStopped) {
                poll()
            }
        }

        refreshRef.current = () => {
            startedAt = Date.now()
            isStopped = false
            setIsTimedOut(false)
            poll()
        }

        document.addEventListener("visibilitychange", onVisibilityChange)
        poll()
        return () => {
            isCancelled = true
            clearTimeout(timeoutId)
            document.removeEventListener("visibilitychange", onVisibilityChange)
        }
    }, [enabled, taskId, subTaskId, taskUserId])

    const refresh = useCallback(() => refreshRef.current(), [])

    return {isChecked, isTimedOut, refresh}
}
